import React, { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { FaEdit, FaPause, FaTrash } from "react-icons/fa";
import dayjs from "dayjs";

// MUI
import Button from "@mui/material/Button";

// Components
import ProfilePicture from "../../components/ProfilePicture";

// Types
import { User, UserNotificationSettings } from "../../../../types";

// Styles
import styles from './style.module.scss';
import Divider from "../../../../components/divider";
import ToggleSwitch from "../../../../components/ToggleSwitch";
import Typography from "../../../../components/Typography";
import MenuButton from "../../../../components/MenuButton";
import DataSettingsModal from "../../components/DataSettingsModal";

// Utils
import * as urls from '../../../../utils/urls';
import useCsrfToken from '../../../../utils/hooks/csfrToken';

const defaultNotifications = {
  browser_notifications: false,
  email_notifications: false,
  email_updates: false,
};

const HIDE_BILLING_DETAILS = true;

interface AccountSettingsProps {
  user: User | null;
  onUpdateUser?: (updatedUser: Partial<User>) => void;
}

const AccountSettings: React.FC<AccountSettingsProps> = ({ user, onUpdateUser }) => {
  // Translations
  const { t } = useTranslation();
  
  // CSRF Token
  const csrfToken = useCsrfToken();

  // State
  const [firstName, setFirstName] = useState<string | null>(user?.first_name || null);
  const [lastName, setLastName] = useState<string | null>(user?.last_name || null);
  const [pushNotificationsEnabled, setPushNotificationsEnabled] = useState<boolean>(() => {
    return user?.push_notifications_enabled ?? false;
  });
  const [hasChanged, setHasChanged] = useState<boolean>(false);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [notifications, setNotifications] = useState<UserNotificationSettings>(defaultNotifications);

  const [isDataSettingsOpen, setIsDataSettingsOpen] = useState<boolean>(false);

  // Memoized values
  const subscriptionOptions = useMemo(() => {
    return [
      { label: t('actions.change'), value: 'change', icon: <FaEdit size={16} /> },
      { label: t('actions.pause'), value: 'pause', icon: <FaPause size={16} /> },
      { label: '---', value: 'divider', divider: true },
      { label: t('actions.cancel'), value: 'cancel', icon: <FaTrash size={16} />, danger: true },
    ];
  }, [t]);

  // Handlers
  const handleFirstNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFirstName(e.target.value);
    if (e.target.value !== user?.first_name && e.target.value !== '') {
      setHasChanged(true);
    } else {
      setHasChanged(false);
    }
  };

  const handleLastNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setLastName(e.target.value);
    if (e.target.value !== user?.last_name && e.target.value !== '') {
      setHasChanged(true);
    } else {
      setHasChanged(false);
    }
  };

  const handleOpenDataSettings = useCallback(() => {
    setIsDataSettingsOpen(true);
  }, []);

  const handleChangeNotifications = useCallback((checked: boolean, field?: string) => {
    if (field) {
      setNotifications(prev => ({
        ...prev,
        [field]: checked,
      }));
    }
  }, []);

  const handleBrowserPushNotificationsChange = useCallback(async (checked: boolean) => {
    setPushNotificationsEnabled(checked);
    
    try {
      const options = {
        method: 'PATCH',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
          'X-CSRF-Token': csrfToken,
        },
        body: JSON.stringify({ 
          user: { 
            push_notifications_enabled: checked
          } 
        }),
      };

      const response = await fetch(urls.profileUpdateUrl(), options);
      
      if (response.ok) {
        const data = await response.json();
      } else {
        const errorData = await response.json();
        console.error('Error updating push notifications setting:', errorData);
        setPushNotificationsEnabled(!checked);
      }
    } catch (error) {
      console.error('Network error:', error);
      setPushNotificationsEnabled(!checked);
    }
  }, [csrfToken]);

  const handleSubscriptionAction = useCallback((action: 'change' | 'pause' | 'cancel') => {
    switch (action) {
      case 'change':
        // TODO: open change subscription modal
        break;
      case 'pause':
        // TODO: open pause subscription modal
        break;
      case 'cancel':
        // TODO: open cancel subscription modal
        break;
    }
  }, []);

  const handleSave = useCallback(async () => {
    if (!firstName && !lastName) return;
    
    setIsSaving(true);
    
    try {
      const options = {
        method: 'PATCH',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
          'X-CSRF-Token': csrfToken,
        },
        body: JSON.stringify({ 
          user: { 
            first_name: firstName || '', 
            last_name: lastName || '' 
          } 
        }),
      };

      const response = await fetch(urls.profileUpdateUrl(), options);
      
      if (response.ok) {
        const data = await response.json();
        setHasChanged(false);
        
        // Update the user context to reflect changes in sidebar
        if (onUpdateUser && data.user) {
          onUpdateUser({
            first_name: data.user.first_name,
            last_name: data.user.last_name,
            full_name: data.user.full_name
          });
        }
      } else {
        const errorData = await response.json();
        console.error('Error updating profile:', errorData);
        // TODO: Show error message/toast
      }
    } catch (error) {
      console.error('Network error:', error);
    } finally {
      setIsSaving(false);
    }
  }, [firstName, lastName, csrfToken]);

  const formatDate = useCallback(() => {
    return dayjs().add(1, 'month').format('MMM DD, YYYY');
  }, []);

  const getNameOnCard = useCallback(() => {
    // TODO: get name on card from API
    return "John Doe";
  }, [firstName, lastName]);

  const getCardDetail = useCallback(() => {
    // TODO: get card detail from API
    return "VISA *2234";
  }, [t, formatDate]);

  // Styles
  const rootClasses = "flex flex-col gap-2 overflow-y-auto pe-2 pb-2";
  return (
    <div className={rootClasses}>
      <ProfilePicture currentAvatar={user?.avatar || null} onUpdateUser={onUpdateUser} />

      <div className={styles.nameContainer}>
        <div className={styles.nameContainer_controls}>

          <div className={styles[`nameContainer_control`]}>
            <label htmlFor="firstName">
              {t('profile.settings.first_name')}
              <span className={styles.nameContainer_required}>*</span>
            </label>

            <input
              type="text"
              id="firstName"
              value={firstName || ''}
              onChange={handleFirstNameChange}
              placeholder={t('profile.settings.first_name')}
            />
          </div>

          <div className={styles[`nameContainer_control`]}>
            <label htmlFor="lastName">
              {t('profile.settings.last_name')}
              <span className={styles.nameContainer_required}>*</span>
            </label>

            <input
              type="text"
              id="lastName"
              value={lastName || ''}
              onChange={handleLastNameChange}
              placeholder={t('profile.settings.last_name')}
            />
          </div>
        </div>

        {hasChanged && (
          <Button
            variant='contained'
            color='primary'
            className={styles.nameContainer_saveButton}
            onClick={handleSave}
            disabled={!hasChanged || isSaving}
          >
            {isSaving ? t('actions.saving') : t('actions.save')}
          </Button>
        )}
      </div>

      <Button
        variant='text'
        color='primary'
        className={`${styles.dataSettingsButton} dark:text-white`}
        onClick={handleOpenDataSettings}
      >
        {t('profile.settings.data_settings')}
      </Button>

      <div className={styles.sectionContainer}>
        <h2>{t('profile.settings.notifications.title')}</h2>

        <Divider />

        <div className={styles.notificationControl}>
          <Typography variant="p">
            {t('profile.settings.notifications.browser_notifications')}
          </Typography>

          <ToggleSwitch
            field="browser_notifications"
            checked={pushNotificationsEnabled}
            onChange={handleBrowserPushNotificationsChange}
          />
        </div>

        {/* <div className={styles.notificationControl}>
          <Typography variant="p">
            {t('profile.settings.notifications.email_notifications')}
          </Typography>

          <ToggleSwitch
            field="email_notifications"
            checked={notifications.email_notifications}
            onChange={handleChangeNotifications}
          />
        </div>

        <div className={styles.notificationControl}>
          <Typography variant="p">
            {t('profile.settings.notifications.email_updates')}
          </Typography>

          <ToggleSwitch
            field="email_updates"
            checked={notifications.email_updates}
            onChange={handleChangeNotifications}
          />
        </div> */}
      </div>

      {!HIDE_BILLING_DETAILS && (
        <div className={styles.sectionContainer}>
          <h2>{t('profile.settings.billing.title')}</h2>

          <Divider />

          <div className={styles.billingControl}>
            <div>
              <Typography variant="p">
                {t('profile.settings.billing.subscription')}
              </Typography>

              <Typography variant="span" className={styles.billingControl_subText}>
                {t('profile.settings.billing.next_renewal', { date: formatDate() })}
              </Typography>
            </div>

            <div className={styles.billingControl_buttons}>
              <MenuButton options={subscriptionOptions} onSelect={handleSubscriptionAction}>
                {t('actions.actions')}
              </MenuButton>

              <Button variant="text" color="primary" onClick={() => { }}>
                {t('actions.change')}
              </Button>
            </div>
          </div>

          <div className={styles.billingControl}>
            <div>
              <Typography variant="p">
                {getNameOnCard()}
              </Typography>

              <Typography variant="span" className={styles.billingControl_subText}>
                {getCardDetail()}
              </Typography>
            </div>

            <div className={styles.billingControl_buttons}>
              <Button variant="text" color="primary" onClick={() => { }}>
                {t('actions.change')}
              </Button>
            </div>
          </div>
        </div>
      )}

      <DataSettingsModal
        user={user}
        isOpen={isDataSettingsOpen}
        onClose={() => setIsDataSettingsOpen(false)}
        onUpdateUser={onUpdateUser}
      />
    </div>
  );
};

export default AccountSettings;
